import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Eye,
  Download,
  User,
  Check,
  Loader2,
  AlertTriangle,
  HelpCircle,
  Magnet,
  Cloud,
  CloudOff,
} from 'lucide-react';
import { useProjectStore } from '../../state/projectStore';
import { useAuthStore } from '../../state/authStore';
import { formatTimeNl } from '../../lib/format';
import { t } from '../../i18n';
import { ShortcutsDialog } from './ShortcutsDialog';
import './top-bar.css';

export function TopBar({ onFitZoom }: { onFitZoom: () => void }) {
  const navigate = useNavigate();
  const project = useProjectStore((s) => s.project);
  const saveStatus = useProjectStore((s) => s.saveStatus);
  const lastSavedAt = useProjectStore((s) => s.lastSavedAt);
  const cloudSyncStatus = useProjectStore((s) => s.cloudSyncStatus);
  const cloudSyncError = useProjectStore((s) => s.cloudSyncError);
  const canUndo = useProjectStore((s) => s.canUndo);
  const canRedo = useProjectStore((s) => s.canRedo);
  const undo = useProjectStore((s) => s.undo);
  const redo = useProjectStore((s) => s.redo);
  const zoom = useProjectStore((s) => s.zoom);
  const setZoom = useProjectStore((s) => s.setZoom);
  const setTitle = useProjectStore((s) => s.setTitle);
  const toggleSnapEnabled = useProjectStore((s) => s.toggleSnapEnabled);
  const authStatus = useAuthStore((s) => s.status);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(project?.title ?? '');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  if (!project) return null;

  const commitTitle = () => {
    setEditingTitle(false);
    if (titleDraft.trim() && titleDraft !== project.title) setTitle(titleDraft);
  };

  return (
    <div className="top-bar">
      <div className="top-bar-section">
        <Link to="/projecten" className="top-bar-back" title={t('editor.topbar.back')}>
          <ArrowLeft size={18} />
        </Link>
        {editingTitle ? (
          <input
            className="top-bar-title-input"
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitTitle();
              if (e.key === 'Escape') {
                setTitleDraft(project.title);
                setEditingTitle(false);
              }
            }}
          />
        ) : (
          <button
            className="top-bar-title"
            onClick={() => {
              setTitleDraft(project.title);
              setEditingTitle(true);
            }}
          >
            {project.title || t('editor.topbar.untitled')}
          </button>
        )}
        <SaveStatusBadge status={saveStatus} lastSavedAt={lastSavedAt} />
        {project.ownerMode === 'cloud' ? (
          <CloudSyncBadge status={cloudSyncStatus} error={cloudSyncError} />
        ) : null}
      </div>

      <div className="top-bar-section">
        <button
          className="top-bar-icon-btn"
          onClick={undo}
          disabled={!canUndo}
          title={t('editor.topbar.undo')}
        >
          <Undo2 size={16} />
        </button>
        <button
          className="top-bar-icon-btn"
          onClick={redo}
          disabled={!canRedo}
          title={t('editor.topbar.redo')}
        >
          <Redo2 size={16} />
        </button>
        <button
          className={project.settings.snapEnabled ? 'top-bar-icon-btn active' : 'top-bar-icon-btn'}
          onClick={toggleSnapEnabled}
          title={t('editor.topbar.snapToggle')}
          aria-pressed={project.settings.snapEnabled}
        >
          <Magnet size={16} />
        </button>
        <div className="top-bar-zoom">
          <button
            className="top-bar-icon-btn"
            onClick={() => setZoom(zoom - 0.1)}
            title={t('editor.topbar.zoomOut')}
          >
            <ZoomOut size={16} />
          </button>
          <span className="top-bar-zoom-value">{Math.round(zoom * 100)}%</span>
          <button
            className="top-bar-icon-btn"
            onClick={() => setZoom(zoom + 0.1)}
            title={t('editor.topbar.zoomIn')}
          >
            <ZoomIn size={16} />
          </button>
          <button
            className="top-bar-icon-btn"
            onClick={onFitZoom}
            title={t('editor.topbar.zoomFit')}
          >
            <Maximize2 size={16} />
          </button>
        </div>
        <button
          className="top-bar-icon-btn"
          onClick={() => setShortcutsOpen(true)}
          title={t('editor.shortcuts.title')}
        >
          <HelpCircle size={16} />
        </button>
      </div>

      <div className="top-bar-section">
        <button className="top-bar-btn" onClick={() => navigate(`/preview/${project.id}`)}>
          <Eye size={16} />
          {t('editor.topbar.preview')}
        </button>
        <button className="top-bar-btn primary" onClick={() => navigate(`/export/${project.id}`)}>
          <Download size={16} />
          {t('editor.topbar.export')}
        </button>
        <Link to="/account" className="top-bar-account" title={t('editor.topbar.account')}>
          <User size={16} />
          {authStatus === 'authenticated' ? t('editor.topbar.account') : t('editor.topbar.guest')}
        </Link>
      </div>

      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
}

function CloudSyncBadge({ status, error }: { status: string; error: string | null }) {
  if (status === 'syncing') {
    return (
      <span className="top-bar-save-status saving" title={t('editor.topbar.cloudSyncing')}>
        <Loader2 size={13} className="spin" /> {t('editor.topbar.cloudSyncing')}
      </span>
    );
  }
  if (status === 'offline') {
    return (
      <span className="top-bar-save-status unsaved" title={t('editor.topbar.cloudOffline')}>
        <CloudOff size={13} /> {t('editor.topbar.cloudOffline')}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="top-bar-save-status error" title={error ?? t('editor.topbar.cloudError')}>
        <AlertTriangle size={13} /> {t('editor.topbar.cloudError')}
      </span>
    );
  }
  if (status === 'synced') {
    return (
      <span className="top-bar-save-status saved" title={t('editor.topbar.cloudSynced')}>
        <Cloud size={13} /> {t('editor.topbar.cloudSynced')}
      </span>
    );
  }
  return null;
}

function SaveStatusBadge({ status, lastSavedAt }: { status: string; lastSavedAt: string | null }) {
  if (status === 'saving') {
    return (
      <span className="top-bar-save-status saving">
        <Loader2 size={13} className="spin" /> {t('editor.topbar.saving')}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="top-bar-save-status error">
        <AlertTriangle size={13} /> {t('editor.topbar.saveError')}
      </span>
    );
  }
  if (status === 'unsaved') {
    return <span className="top-bar-save-status unsaved">{t('editor.topbar.unsaved')}</span>;
  }
  return (
    <span className="top-bar-save-status saved">
      <Check size={13} />
      {lastSavedAt
        ? t('editor.topbar.savedAt', { time: formatTimeNl(new Date(lastSavedAt)) })
        : t('editor.topbar.saved')}
    </span>
  );
}
